// Walking the menu tree (R-U7): a section and its groups list types, views and screens at any depth.

import type { MenuGroup, MenuItems, MenuSection } from './protocol.ts';

/** Every group under a node, depth first, with the path of groups that leads to it. */
export function menuGroups(node: MenuItems, path: MenuGroup[] = []): { group: MenuGroup; path: MenuGroup[] }[] {
  return (node.groups ?? []).flatMap((g) => [{ group: g, path }, ...menuGroups(g, [...path, g])]);
}
/** The node itself and every group under it. */
const nodes = (node: MenuItems): MenuItems[] => [node, ...menuGroups(node).map((x) => x.group)];
export const menuTypeIds = (node: MenuItems): string[] => nodes(node).flatMap((n) => n.typeIds);
export const menuViews = (node: MenuItems) => nodes(node).flatMap((n) => n.views ?? []);
export const menuScreens = (node: MenuItems) => nodes(node).flatMap((n) => n.screens ?? []);
export const menuEmpty = (node: MenuItems): boolean => !menuTypeIds(node).length && !menuViews(node).length && !menuScreens(node).length;

/** Keys of sections and groups of a menu: a group is found by its key like a section. */
export const menuKeys = (menu: MenuSection[]): string[] => menu.flatMap((m) => [m.key, ...menuGroups(m).map((x) => x.group.key)]);
export const menuNode = (menu: MenuSection[], key: string): MenuItems | undefined =>
  menu.find((m) => m.key === key) ?? menu.flatMap((m) => menuGroups(m)).find((x) => x.group.key === key)?.group;

/** Where a type, a view or a screen sits: its section and the groups down to the node that lists it. */
export function menuPlace(menu: MenuSection[], has: (n: MenuItems) => boolean): { section: MenuSection; path: MenuGroup[] } | undefined {
  for (const m of menu) {
    if (has(m)) return { section: m, path: [] };
    const g = menuGroups(m).find((x) => has(x.group));
    if (g) return { section: m, path: [...g.path, g.group] };
  }
  return undefined;
}

/** Keeps what `keep` lets through at every depth; a group left with nothing disappears. */
export function filterMenu<T extends MenuItems>(node: T, keep: { type?: (id: string) => boolean; view?: (v: NonNullable<MenuItems['views']>[number]) => boolean; screen?: (s: NonNullable<MenuItems['screens']>[number]) => boolean }): T {
  return {
    ...node,
    typeIds: node.typeIds.filter((t) => !keep.type || keep.type(t)),
    ...(node.views ? { views: node.views.filter((v) => !keep.view || keep.view(v)) } : {}),
    ...(node.screens ? { screens: node.screens.filter((s) => !keep.screen || keep.screen(s)) } : {}),
    ...(node.groups ? { groups: node.groups.map((g) => filterMenu(g, keep)).filter((g) => !menuEmpty(g)) } : {}),
  };
}
